import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/cn";

export function Modal({
  open: openProp,
  visible,
  title,
  onCancel,
  onOk,
  footer,
  children,
  width,
  className,
  okText = "OK",
  cancelText = "Cancel",
  confirmLoading,
  destroyOnClose,
}) {
  const open = openProp ?? visible ?? false;
  const [innerLoading, setInnerLoading] = useState(false);
  const loading = confirmLoading ?? innerLoading;

  useEffect(() => {
    if (!open) setInnerLoading(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) {
    if (destroyOnClose) return null;
    return null;
  }

  const maxW =
    typeof width === "number" ? { maxWidth: Math.min(width, 1200) } : undefined;
  const widthClass =
    typeof width === "string" && width.includes("px")
      ? undefined
      : "max-w-2xl";

  const content = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-[1px]"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className={cn(
          "w-full rounded-2xl border border-slate-200 bg-white shadow-2xl",
          widthClass,
          className
        )}
        style={maxW}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {title != null && (
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            <button
              type="button"
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              onClick={onCancel}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        )}
        <div className="max-h-[min(70vh,800px)] overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer === null ? null : footer !== undefined ? (
          footer
        ) : (
          <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
            <button
              type="button"
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              onClick={onCancel}
            >
              {cancelText}
            </button>
            <button
              type="button"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
              onClick={async () => {
                if (!onOk) return;
                if (confirmLoading === undefined) setInnerLoading(true);
                try {
                  await Promise.resolve(onOk());
                } catch {
                  /* keep open */
                } finally {
                  if (confirmLoading === undefined) setInnerLoading(false);
                }
              }}
            >
              {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : null}
              {okText}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

Modal.defaultFooter = undefined;
